import { createRef, RefObject, useRef, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { ChipButton } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics';

import ToolsSlide from '../ToolsSlide';
import { ButtonsWrapper, ContentWrapper, Copy, CopyWrapper, Heading, Wrapper } from './styles';
import { MentalHealthSectionTitle, MentalHealthSlide } from './types';

export type MentalHealthToolsProps = {
	toolsSections: Record<MentalHealthSectionTitle, MentalHealthSlide>;
	toolsHeading: MessageDescriptor;
	toolsCopy?: MessageDescriptor;
	analyticsPrefix?: string;
	hideFreeSampleLabel?: boolean;
	toolsCTAConfig?: {
		label: MessageDescriptor;
		onClick: () => void;
	};
	toolImageBorderBakedIn?: boolean;
};

const MentalHealthTools = ({
	toolsSections,
	toolsHeading,
	toolsCopy,
	analyticsPrefix = 'Homepage',
	hideFreeSampleLabel,
	toolsCTAConfig,
	toolImageBorderBakedIn = false,
}: MentalHealthToolsProps) => {
	const tabRefs = useRef<Record<MentalHealthSectionTitle, RefObject<HTMLButtonElement>>>(
		Object.keys(toolsSections).reduce(
			(acc, item) => ({
				...acc,
				[item]: createRef(),
			}),
			{},
		),
	);

	const [currentSection, setCurrentSection] = useState<MentalHealthSectionTitle>(
		Object.keys(toolsSections)[0],
	);
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const onClick = (sectionTitle: MentalHealthSectionTitle) => {
		logEvent({
			eventName: `${analyticsPrefix} : Tools : Section : Changed`,
			eventProps: {
				section: sectionTitle,
			},
		});
		setCurrentSection(sectionTitle);
	};

	const onKeydown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		const currentSectionIndex = Object.keys(toolsSections).indexOf(currentSection);
		const lastSectionIndex = Object.keys(toolsSections).length - 1;

		const selectTab = (index: number) => {
			const sectionTitle = Object.keys(toolsSections)[index];

			setCurrentSection(sectionTitle);
			tabRefs.current[sectionTitle]?.current?.focus();
		};

		switch (event.key) {
			case 'ArrowLeft':
				selectTab(currentSectionIndex === 0 ? lastSectionIndex : currentSectionIndex - 1);
				break;
			case 'ArrowRight':
				selectTab(currentSectionIndex === lastSectionIndex ? 0 : currentSectionIndex + 1);
				break;
			default:
				break;
		}
	};

	return (
		<Wrapper>
			<CopyWrapper>
				<Heading id="mental-health-tablist">{formatMessage(toolsHeading)}</Heading>
				{toolsCopy && <Copy>{formatMessage(toolsCopy)}</Copy>}
			</CopyWrapper>
			<ContentWrapper>
				<ButtonsWrapper role="tablist" aria-labelledby="mental-health-tablist">
					{
						// TODO: refactor this code to follow best practices
						// eslint-disable-next-line react-hooks/refs
						Object.entries(toolsSections).map(([sectionTitle, section]) => (
							<ChipButton
								key={formatMessage(section.title)}
								focusRingVariant="dark"
								activeBackgroundColor="buttonPrimaryBg"
								inactiveBackgroundColor="gray1"
								inactiveTextColor="blackAlpha80"
								isActive={currentSection === sectionTitle}
								role="tab"
								id={`tab-${sectionTitle}`}
								aria-controls={`tabpanel-${sectionTitle}`}
								aria-selected={currentSection === sectionTitle}
								buttonRef={tabRefs.current[sectionTitle]}
								onPress={() => onClick(sectionTitle as MentalHealthSectionTitle)}
								onKeyDown={e => onKeydown(e)}
								{...(currentSection !== sectionTitle && { tabIndex: -1 })}
							>
								{formatMessage(section.title)}
							</ChipButton>
						))
					}
				</ButtonsWrapper>
				{Object.entries(toolsSections).map(([sectionTitle, section]) => (
					<ToolsSlide
						tabIndex={0}
						role="tabpanel"
						aria-labelledby={`tab-${sectionTitle}`}
						id={`tabpanel-${sectionTitle}`}
						key={`slide-for-${sectionTitle}`}
						slide={section}
						sectionTitle={sectionTitle}
						currentSection={currentSection}
						analyticsPrefix={analyticsPrefix}
						hideFreeSampleLabel={hideFreeSampleLabel}
						ctaConfig={toolsCTAConfig}
						borderBakedIn={toolImageBorderBakedIn}
					/>
				))}
			</ContentWrapper>
		</Wrapper>
	);
};

export default MentalHealthTools;
